/**
 * ProLockedSection — desenfoca UNA tarjeta y le pone encima el reclamo de Pro.
 *
 * Es el patrón que ya usaba la tarjeta de backfill de Automatizaciones,
 * extraído al abrirse las páginas del recorrido guiado: ahora ya no se gatea la
 * página entera sino los dos o tres bloques que de verdad cuestan dinero, y ese
 * mismo envoltorio hacía falta en Métricas, SEO Local y Publicaciones.
 *
 * Lo que hace que funcione, y que es fácil romper:
 *
 * - El reclamo va en un hermano `absolute inset-0 z-10`, NO dentro del bloque
 *   desenfocado. Si se mete dentro, el `blur-sm` se lo come a él también.
 * - El bloque real lleva `inert` + `aria-hidden` además del desenfoque. El
 *   desenfoque es cosmético: sin `inert` se puede seguir tabulando hasta los
 *   controles de debajo, y un lector de pantalla los lee como si nada.
 * - **Las consultas de dentro tienen que ir con `enabled: !locked`.** El
 *   desenfoque no evita que se disparen, así que un bloque bloqueado con sus
 *   consultas vivas llena la consola de 402 detrás del cristal.
 * - **Y lo que se desenfoca deberían ser datos de EJEMPLO, no los del
 *   negocio.** `blur-sm` es una clase de CSS: se quita con dos clics en el
 *   inspector. Quien lo haga no debe llevarse gratis lo que estamos cobrando
 *   ni, peor, creerse que esas cifras son las suyas. Para eso está
 *   `sampleLabel`, que planta la advertencia DENTRO del bloque desenfocado:
 *   aparece legible exactamente cuando alguien quita el cristal.
 *
 * No monta ningún modal: `onUpgrade` lo decide quien lo usa, porque no siempre
 * es el mismo (la tarjeta de precios en unos sitios, terminar el trial en
 * otros).
 */
import CtaLink from '@/components/CtaLink';
import { Button } from '@/components/ui/button';

export default function ProLockedSection({
  locked,
  title,
  description,
  cta,
  onUpgrade,
  // Id del CTA en `lib/ctaRegistry.js`. Lo pone quien monta el bloque, no este
  // componente: son cuatro bloqueos distintos (SEO, conversión, búsquedas,
  // competencia) y saber CUÁL empuja a pagar es justo lo que se quiere medir.
  ctaId,
  // Texto de «esto es un ejemplo». Solo se pasa cuando el contenido de debajo
  // SON datos de ejemplo de verdad: una etiqueta que mintiera sería peor que no
  // ponerla.
  sampleLabel,
  children,
}) {
  if (!locked) return children;

  return (
    <div className="relative">
      <div className="absolute inset-0 z-10 flex items-center justify-center p-4">
        <div className="text-center space-y-3 max-w-sm md:max-w-2xl">
          <div>
            <h3 className="text-xl md:text-2xl font-extrabold text-slate-900 mb-1 leading-tight">
              {title}
            </h3>
            {description && (
              <p className="text-sm text-slate-600">{description}</p>
            )}
          </div>
          {onUpgrade && cta && (
            <CtaLink
              as={Button}
              cta={ctaId}
              // El id se le pasa al callback, no solo al tracking del click: es
              // lo que permite que el modal de precios que se abre a
              // continuación le atribuya el alta a ESTE muro y no a sí mismo
              // (decisiones §124).
              onClick={() => onUpgrade(ctaId)}
              className="bg-[#feae2c] hover:bg-[#e89d1a] text-black font-semibold"
            >
              {cta}
            </CtaLink>
          )}
        </div>
      </div>
      <div inert="" aria-hidden className="blur-sm select-none pointer-events-none">
        {sampleLabel && (
          // Dentro del desenfoque y no encima: mientras el cristal está puesto
          // es un borrón más, y se lee justo cuando alguien lo quita, que es el
          // único momento en el que hace falta.
          <div className="absolute top-2 right-2 z-0 rounded-full bg-slate-900/85 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
            {sampleLabel}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
